import React, { useEffect, useState } from 'react';
import { GetUserVulnerabilityData, GetVulnerabilityData, PostUserVulnerabilityData } from 'services/MyRacService';
import { loggerFunc } from 'utils/logger';
import PageTitle from 'shared/components/PageTitle/PageTitle';
import Spinner from 'components/Spinner';
import GreenCheckboxList from 'components/GreenCheckboxList/GreenCheckboxList';
import { useNavigate } from 'react-router-dom';
import { MARKETING_PERMISSION } from 'constants/routeConstants';
import { ToastType, TToast } from 'components/Toast/Toast';
import trackEvent from 'services/AnalyticsService';
import styles from './VulnerableCustomers.module.scss';

type TVulnerability = {
  id: number;
  label: string;
}

const logger = loggerFunc('VulnerableCustomers');

const VulnerableCustomers: React.FC = () => {
  const navigate = useNavigate();

  const [vulnerabilities, setVulnerabilities] = useState<TVulnerability[]>([]);
  const [selected, setSelected] = useState<TVulnerability[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'pending' | 'success' | 'error' | undefined>()

  const createToast: TToast = {
    msg: 'Help & support updated',
    type: ToastType.SUCCESS,
  };

  const routeToMarketingPage = () => {
    trackEvent('MyRAC_VulnerableCustomers_Page_Saved');
    navigate(MARKETING_PERMISSION, { state: { toast: createToast } })
  };

  const handleSubmit = async () => {
    setSaveStatus('pending');

    try {
      const selectedIds = selected.map((item) => item.id);
      const response = await PostUserVulnerabilityData(selectedIds);
      if (response?.payload === true) routeToMarketingPage();
      else setSaveStatus('error');
    } catch (err) {
      logger.debug('Failed to save vulnerabilities:', err);
      setSaveStatus('error');
    }
  }

  const handleResetSelection = async () => {
    setSaveStatus('pending');

    try {
      const response = await PostUserVulnerabilityData([]);
      if (response?.payload === true) {
        setSelected([]);
        routeToMarketingPage();
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      logger.debug('Failed to save vulnerabilities:', err);
      setSaveStatus('error');
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vulnerabilityData, userVulnerabilityData] = await Promise.all([
          GetVulnerabilityData(),
          GetUserVulnerabilityData(),
        ]);

        if (vulnerabilityData) setVulnerabilities(vulnerabilityData);
        const selectedItems = vulnerabilityData.filter((item: { id: string}) => userVulnerabilityData.includes(item.id));
        if (userVulnerabilityData) setSelected(selectedItems);

        trackEvent('MyRAC_VulnerableCustomers_Page_View');
      } catch (err) {
        logger.debug(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <>
      <PageTitle text="Help & support for your breakdown cover" />
      <div className={styles.container}>
        {saveStatus === 'error' && <div className="text-center text-danger">Sorry! There was an error while fetching the data.</div>}
        <div className={loading ? 'd-flex flex-column align-items-center justify-content-center min-vh-75 pb-12' : ''}>
          {loading ? <Spinner bootSpinner />
            : (
              <>
                <p className={styles.subheader}>
                  To help us provide the best support during a breakdown, please select
                  all options that apply to you:
                </p>
                <GreenCheckboxList
                  options={vulnerabilities}
                  selected={selected}
                  setSelected={setSelected}
                />
                <div className="mb-5">
                  <button
                    type="button"
                    disabled={saveStatus === 'pending'}
                    className={styles.saveButton}
                    onClick={handleSubmit}
                  >
                    {saveStatus === 'pending' ? <i className="fas fa-spinner fa-pulse" /> : 'Update'}
                  </button>

                  {saveStatus !== 'pending' && (
                    <button
                      type="button"
                      className={styles.cancelButton}
                      onClick={handleResetSelection}
                    >
                      None apply
                    </button>
                  )}
                </div>
              </>
            )}
        </div>
      </div>
    </>
  );
};

export default VulnerableCustomers;
